import { useState, useEffect, useCallback } from 'react';
import { getItems, createItem, deleteItem, updateItem, YARDS_TO_METERS } from './fitnessApi';
import { RESUME_TTL_MS } from './SwimmingWorkout';
import { buildWorkoutPatch } from './workoutEdit';

const WORKOUTS_LIST = 'SwimmingWorkouts';
const WEIGHINS_LIST = 'FitnessWeighIns';

function safeParse(json) {
  try { return JSON.parse(json) || []; } catch { return []; }
}

function SwimmingHistory({ getToken, userEmail }) {
  const [view, setView] = useState('history'); // history, stats, prs, weighin
  const [workouts, setWorkouts] = useState([]);
  const [weighIns, setWeighIns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayUnit, setDisplayUnit] = useState(() => localStorage.getItem('dh_displayUnit') || 'm');

  // Weigh-in form
  const [newWeight, setNewWeight] = useState('');
  const [addingWeight, setAddingWeight] = useState(false);

  // Expanded workout
  const [expandedId, setExpandedId] = useState(null);

  // Post-hoc rep editor. `editing` is a deep copy of one workout's sets plus
  // the identifiers the save needs — never the live object, so Cancel is free.
  const [editing, setEditing] = useState(null); // { id, startTime, poolUnit, sets }
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const token = await getToken();
      const [wk, wi] = await Promise.all([
        getItems(token, WORKOUTS_LIST),
        getItems(token, WEIGHINS_LIST),
      ]);
      const myWorkouts = wk
        .filter(w => w.CreatedByEmail === userEmail)
        .map(w => ({
          id: w.id,
          title: w.Title || '',
          templateName: w.TemplateName || '',
          sets: safeParse(w.Sets),
          totalDistanceMeters: w.TotalDistanceMeters || 0,
          poolLength: w.PoolLength || 25,
          poolUnit: w.PoolUnit || 'm',
          durationSeconds: w.DurationSeconds || 0,
          date: w.WorkoutDate || w.Created || '',
          startTime: w.WorkoutStartTime || '',
          endTime: w.WorkoutEndTime || '',
        }))
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      setWorkouts(myWorkouts);

      const myWeighIns = wi
        .filter(w => w.CreatedByEmail === userEmail)
        .map(w => ({
          id: w.id,
          weight: w.WeightLbs || 0,
          date: w.WeighDate || w.Created || '',
        }))
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      setWeighIns(myWeighIns);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, userEmail]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function convertDist(meters) {
    if (displayUnit === 'yd') return Math.round(meters / YARDS_TO_METERS);
    return Math.round(meters);
  }

  function toggleUnit() {
    const next = displayUnit === 'm' ? 'yd' : 'm';
    setDisplayUnit(next);
    localStorage.setItem('dh_displayUnit', next);
  }

  // Editable = recent enough that the swimmer still remembers the session:
  // the same 3h window the workout screen uses for resume. Rows without both
  // stamps predate the stamp columns and have no window to correct.
  function isEditable(w) {
    if (!w.startTime || !w.endTime) return false;
    const end = new Date(w.endTime).getTime();
    return Number.isFinite(end) && Date.now() - end < RESUME_TTL_MS;
  }

  function openEditor(w) {
    const sets = JSON.parse(JSON.stringify(w.sets));
    // Timed sets get their rest arrays normalized to repTimes' length up
    // front, so the table's rows and the parallel-array guarantee hold while
    // editing — buildWorkoutPatch re-asserts it on save.
    for (const s of sets) {
      if (!Array.isArray(s.repTimes) || s.repTimes.length === 0) continue;
      const pad = arr => {
        const out = Array.isArray(arr) ? arr.slice(0, s.repTimes.length) : [];
        while (out.length < s.repTimes.length) out.push(null);
        return out;
      };
      s.repRests = pad(s.repRests);
      s.repRestTargets = pad(s.repRestTargets);
    }
    setEditing({ id: w.id, startTime: w.startTime, poolUnit: w.poolUnit, sets });
  }

  function updateRep(si, ri, field, value) {
    setEditing(prev => {
      const sets = prev.sets.map((s, i) => {
        if (i !== si) return s;
        const next = { ...s };
        if (field === 'time') next.repTimes = s.repTimes.map((t, j) => (j === ri ? value : t));
        else next.repRests = s.repRests.map((r, j) => (j === ri ? value : r));
        return next;
      });
      return { ...prev, sets };
    });
  }

  function addRep(si) {
    setEditing(prev => {
      const sets = prev.sets.map((s, i) => {
        if (i !== si) return s;
        return {
          ...s,
          repTimes: [...(s.repTimes || []), ''],
          repRests: [...(s.repRests || []), null],
          repRestTargets: [...(s.repRestTargets || []), null],
        };
      });
      return { ...prev, sets };
    });
  }

  function removeRep(si, ri) {
    setEditing(prev => {
      const sets = prev.sets.map((s, i) => {
        if (i !== si) return s;
        return {
          ...s,
          repTimes: s.repTimes.filter((_, j) => j !== ri),
          repRests: s.repRests.filter((_, j) => j !== ri),
          repRestTargets: s.repRestTargets.filter((_, j) => j !== ri),
        };
      });
      return { ...prev, sets };
    });
  }

  // The one save path. buildWorkoutPatch returns Sets and its window as a
  // single object or throws; updateItem receives that object whole. Writing
  // one without the other is not something this code can express.
  async function handleSaveEdit() {
    setSavingEdit(true);
    setError(null);
    try {
      const patch = buildWorkoutPatch({
        workoutStartTime: editing.startTime,
        poolUnit: editing.poolUnit,
        sets: editing.sets,
      });
      const token = await getToken();
      await updateItem(token, WORKOUTS_LIST, editing.id, patch);
      setEditing(null);
      await fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingEdit(false);
    }
  }

  async function handleDeleteWorkout(id) {
    if (!window.confirm('Delete this workout?')) return;
    try {
      const token = await getToken();
      await deleteItem(token, WORKOUTS_LIST, id);
      setWorkouts(prev => prev.filter(w => w.id !== id));
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleAddWeighIn(e) {
    e.preventDefault();
    if (!newWeight) return;
    setAddingWeight(true);
    setError(null);
    try {
      const token = await getToken();
      const now = new Date().toISOString();
      await createItem(token, WEIGHINS_LIST, {
        Title: new Date().toLocaleDateString(),
        WeightLbs: parseFloat(newWeight),
        WeighDate: now,
        CreatedByEmail: userEmail,
      });
      setNewWeight('');
      await fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingWeight(false);
    }
  }

  async function handleDeleteWeighIn(id) {
    try {
      const token = await getToken();
      await deleteItem(token, WEIGHINS_LIST, id);
      setWeighIns(prev => prev.filter(w => w.id !== id));
    } catch (err) {
      setError(err.message);
    }
  }

  // Stats calculations
  function getStats(period) {
    const now = new Date();
    let start;
    if (period === 'week') {
      start = new Date(now);
      start.setDate(start.getDate() - 7);
    } else if (period === 'month') {
      start = new Date(now);
      start.setMonth(start.getMonth() - 1);
    } else {
      start = new Date(now);
      start.setFullYear(start.getFullYear() - 1);
    }
    const filtered = workouts.filter(w => new Date(w.date) >= start);
    const visits = filtered.length;
    const totalMeters = filtered.reduce((sum, w) => sum + w.totalDistanceMeters, 0);
    return { visits, totalMeters };
  }

  // PR calculations
  function getPRs() {
    const prs = {};
    workouts.forEach(w => {
      w.sets.forEach(s => {
        // Skip sets flagged as mistimed / cut short — their reps aren't valid PR candidates.
        if (s.partial === true) return;

        const dist = Number(s.actualDistance || s.distance);
        const stroke = s.actualStroke || s.stroke;
        const key = `${dist}-${stroke}`;

        // Check individual rep times first (each rep is a candidate)
        let times = [];
        if (s.repTimes && s.repTimes.length > 0) {
          s.repTimes.forEach(t => { if (Number(t) > 0) times.push(Number(t)); });
        } else if (Number(s.timeSeconds) > 0) {
          times.push(Number(s.timeSeconds));
        }

        // Drop implausibly-fast outliers relative to THIS set's median. A mistimed
        // split reads far below its siblings (e.g. a 74s among 128s "100 Free").
        // Using a fraction of the median generalizes across distances; a hardcoded
        // floor would not. Needs >=2 reps to have a meaningful median.
        if (times.length >= 2) {
          const sorted = [...times].sort((a, b) => a - b);
          const mid = Math.floor(sorted.length / 2);
          const median = sorted.length % 2 === 0
            ? (sorted[mid - 1] + sorted[mid]) / 2
            : sorted[mid];
          times = times.filter(t => t >= median * 0.6);
        }

        times.forEach(time => {
          if (!prs[key] || time < prs[key].time) {
            prs[key] = { distance: dist, stroke, time, date: w.date };
          }
        });
      });
    });
    return Object.values(prs).sort((a, b) => {
      if (a.stroke !== b.stroke) return a.stroke.localeCompare(b.stroke);
      return a.distance - b.distance;
    });
  }

  if (loading) return <p className="Fitness-loading">Loading history...</p>;

  return (
    <div className="SwimHistory">
      {error && <p className="Fitness-error">{error}</p>}

      <div className="SwimHistory-view-tabs">
        {[
          { key: 'history', label: 'History' },
          { key: 'stats', label: 'Stats' },
          { key: 'prs', label: 'PRs' },
          { key: 'weighin', label: 'Weigh-In' },
        ].map(t => (
          <button
            key={t.key}
            className={`SwimHistory-view-tab${view === t.key ? ' SwimHistory-view-tab--active' : ''}`}
            onClick={() => setView(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <button className="Fitness-btn Fitness-btn--small SwimHistory-unit-toggle" onClick={toggleUnit}>
        Showing: {displayUnit === 'm' ? 'Meters' : 'Yards'}
      </button>

      {/* HISTORY VIEW */}
      {view === 'history' && (
        <div className="SwimHistory-list">
          {workouts.length === 0 && <p className="Fitness-empty">No workouts recorded yet.</p>}
          {workouts.map(w => (
            <div key={w.id} className="SwimHistory-card">
              <div className="SwimHistory-card-header" onClick={() => setExpandedId(expandedId === w.id ? null : w.id)}>
                <div>
                  <strong>{w.templateName}</strong>
                  <span className="SwimHistory-card-date">{new Date(w.date).toLocaleDateString()}</span>
                </div>
                <div className="SwimHistory-card-meta">
                  <span>{convertDist(w.totalDistanceMeters)}{displayUnit}</span>
                  {w.durationSeconds > 0 && <span>{Math.floor(w.durationSeconds / 60)}min</span>}
                  <span>{w.poolLength}{w.poolUnit}</span>
                </div>
              </div>
              {expandedId === w.id && (
                <div className="SwimHistory-card-detail">
                  {editing && editing.id === w.id ? (
                    <>
                      <table className="SwimHistory-edit-table">
                        <thead>
                          <tr><th>Set</th><th>Rep</th><th>Time (s)</th><th>Rest (s)</th><th /></tr>
                        </thead>
                        <tbody>
                          {editing.sets.map((s, si) => {
                            const label = `${(s.actualReps || s.reps) > 1 ? `${s.actualReps || s.reps}x` : ''}${s.actualDistance || s.distance} ${s.actualStroke || s.stroke}${s.partial ? ' (partial)' : ''}`;
                            const timed = Array.isArray(s.repTimes) && s.repTimes.length > 0;
                            const repRows = timed ? s.repTimes.map((t, ri) => (
                              <tr key={`${si}-${ri}`}>
                                <td>{ri === 0 ? label : ''}</td>
                                <td>{ri + 1}</td>
                                <td>
                                  <input className="Fitness-input SwimHistory-edit-input" type="number" step="0.1" min="0"
                                    value={t} onChange={e => updateRep(si, ri, 'time', e.target.value)} />
                                </td>
                                <td>
                                  <input className="Fitness-input SwimHistory-edit-input" type="number" step="0.1" min="0"
                                    value={s.repRests[ri] ?? ''} onChange={e => updateRep(si, ri, 'rest', e.target.value)} />
                                </td>
                                <td>
                                  <button className="Fitness-btn-icon Fitness-btn-icon--delete" title="Remove rep"
                                    onClick={() => removeRep(si, ri)}>&times;</button>
                                </td>
                              </tr>
                            )) : (
                              <tr key={`${si}-none`}>
                                <td>{label}</td>
                                <td colSpan="4" className="SwimHistory-edit-untimed">no timed reps</td>
                              </tr>
                            );
                            return [
                              ...repRows,
                              <tr key={`${si}-add`} className="SwimHistory-edit-addrow">
                                <td colSpan="5">
                                  <button className="Fitness-btn Fitness-btn--small" onClick={() => addRep(si)}>
                                    + rep
                                  </button>
                                </td>
                              </tr>,
                            ];
                          })}
                        </tbody>
                      </table>
                      <div className="SwimHistory-edit-actions">
                        <button className="Fitness-btn Fitness-btn--small" onClick={handleSaveEdit} disabled={savingEdit}>
                          {savingEdit ? 'Saving…' : 'Save'}
                        </button>
                        <button className="Fitness-btn Fitness-btn--small Fitness-btn--secondary" onClick={() => setEditing(null)} disabled={savingEdit}>
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {w.sets.map((s, i) => (
                        <div key={i} className="SwimHistory-set">
                          {s.sectionHeader && <em>{s.sectionHeader}: </em>}
                          {(s.actualReps || s.reps) > 1 ? `${s.actualReps || s.reps}x` : ''}
                          {s.actualDistance || s.distance} {s.actualStroke || s.stroke}
                          {s.notes ? ` - ${s.notes}` : ''}
                          {s.repTimes && s.repTimes.length > 0
                            ? ` (${s.repTimes.map((t, ri) => `R${ri + 1}: ${formatTime(t)}${formatRestSuffix(s.repRests, ri)}`).join(', ')})`
                            : s.timeSeconds ? ` (${formatTime(s.timeSeconds)})` : ''}
                          {s.unplanned ? ' [added]' : ''}
                        </div>
                      ))}
                      {isEditable(w) && (
                        <button className="Fitness-btn Fitness-btn--small" onClick={() => openEditor(w)}>
                          Edit
                        </button>
                      )}
                      <button className="Fitness-btn Fitness-btn--small Fitness-btn--danger" onClick={() => handleDeleteWorkout(w.id)}>
                        Delete Workout
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* STATS VIEW */}
      {view === 'stats' && (
        <div className="SwimHistory-stats">
          {['week', 'month', 'year'].map(period => {
            const s = getStats(period);
            return (
              <div key={period} className="SwimHistory-stat-block">
                <h4>Last {period === 'week' ? '7 Days' : period === 'month' ? '30 Days' : '12 Months'}</h4>
                <div className="SwimHistory-stat-row">
                  <span>Pool Visits</span>
                  <strong>{s.visits}</strong>
                </div>
                <div className="SwimHistory-stat-row">
                  <span>Total Distance</span>
                  <strong>{convertDist(s.totalMeters)}{displayUnit}</strong>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* PRS VIEW */}
      {view === 'prs' && (
        <div className="SwimHistory-prs">
          {getPRs().length === 0 && (
            <p className="Fitness-empty">No PRs yet. Enter a time for a set during a workout to track PRs.</p>
          )}
          {getPRs().map((pr, i) => (
            <div key={i} className="SwimHistory-pr-card">
              <div className="SwimHistory-pr-event">{pr.distance} {pr.stroke}</div>
              <div className="SwimHistory-pr-time">{formatTime(pr.time)}</div>
              <div className="SwimHistory-pr-date">{new Date(pr.date).toLocaleDateString()}</div>
            </div>
          ))}
        </div>
      )}

      {/* WEIGH-IN VIEW */}
      {view === 'weighin' && (
        <div className="SwimHistory-weighin">
          <form className="SwimHistory-weighin-form" onSubmit={handleAddWeighIn}>
            <input
              className="Fitness-input"
              type="number"
              step="0.1"
              min="0"
              placeholder="Weight (lbs)"
              value={newWeight}
              onChange={e => setNewWeight(e.target.value)}
              required
            />
            <button className="Fitness-btn" type="submit" disabled={addingWeight}>
              {addingWeight ? 'Saving...' : 'Log Weight'}
            </button>
          </form>

          {weighIns.length === 0 && <p className="Fitness-empty">No weigh-ins recorded yet.</p>}

          {weighIns.length > 1 && (
            <div className="SwimHistory-weighin-trend">
              <span>Latest: <strong>{weighIns[0].weight} lbs</strong></span>
              <span>
                Change: <strong style={{ color: weighIns[0].weight <= weighIns[1].weight ? '#2d7d2d' : '#8B0000' }}>
                  {(weighIns[0].weight - weighIns[1].weight) > 0 ? '+' : ''}
                  {(weighIns[0].weight - weighIns[1].weight).toFixed(1)} lbs
                </strong>
              </span>
              {weighIns.length > 2 && (
                <span>
                  30-day: <strong>
                    {(() => {
                      const thirtyAgo = new Date();
                      thirtyAgo.setDate(thirtyAgo.getDate() - 30);
                      const older = weighIns.filter(w => new Date(w.date) <= thirtyAgo);
                      if (older.length === 0) return 'N/A';
                      const diff = weighIns[0].weight - older[0].weight;
                      return `${diff > 0 ? '+' : ''}${diff.toFixed(1)} lbs`;
                    })()}
                  </strong>
                </span>
              )}
            </div>
          )}

          <div className="SwimHistory-weighin-list">
            {weighIns.map(w => (
              <div key={w.id} className="SwimHistory-weighin-entry">
                <span className="SwimHistory-weighin-date">{new Date(w.date).toLocaleDateString()}</span>
                <span className="SwimHistory-weighin-value">{w.weight} lbs</span>
                <button className="Fitness-btn-icon Fitness-btn-icon--delete" onClick={() => handleDeleteWeighIn(w.id)}>&times;</button>
              </div>
            ))}
          </div>

          {weighIns.length >= 3 && (
            <div className="SwimHistory-weighin-chart">
              <h4>Trend</h4>
              <div className="SwimHistory-weighin-bars">
                {(() => {
                  const recent = [...weighIns].reverse().slice(-20);
                  const min = Math.min(...recent.map(w => w.weight));
                  const max = Math.max(...recent.map(w => w.weight));
                  const range = max - min || 1;
                  return recent.map((w, i) => (
                    <div key={i} className="SwimHistory-weighin-bar-col">
                      <div
                        className="SwimHistory-weighin-bar"
                        style={{ height: `${((w.weight - min) / range) * 100 + 10}%` }}
                        title={`${new Date(w.date).toLocaleDateString()}: ${w.weight} lbs`}
                      />
                      <span className="SwimHistory-weighin-bar-label">
                        {new Date(w.date).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}
                      </span>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Actual rest taken before rep `i`, as " (rest M:SS)". Returns '' when the row
// predates rest capture, when the entry is null (first rep of a workout), or when
// the value is not a positive number — so every pre-Phase-1 row renders exactly
// as it did before.
function formatRestSuffix(repRests, i) {
  if (!Array.isArray(repRests)) return '';
  const r = Number(repRests[i]);
  if (!Number.isFinite(r) || r <= 0) return '';
  const s = Math.round(r);
  return ` (rest ${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')})`;
}

function formatTime(seconds) {
  const s = Number(seconds);
  if (s >= 60) {
    const m = Math.floor(s / 60);
    const rem = (s % 60).toFixed(1);
    return `${m}:${rem.padStart(4, '0')}`;
  }
  return `${s.toFixed(1)}s`;
}

export default SwimmingHistory;
